import React, { useState } from "react";
import { fetchJson } from "../../lib/api";
import type { ApiHabitRow } from "../../types/habit";

interface Props {
  habit: ApiHabitRow;
  onClose: () => void;
  onSubmit: (xpAwarded: number, reflectionNotes: string) => void;
}

export function QuestReflectionModal({ habit, onClose, onSubmit }: Props) {
  const [focusLevel, setFocusLevel] = useState<number>(3);
  const [difficulty, setDifficulty] = useState<number>(3);
  const [reflection, setReflection] = useState<string>("");

  const [evaluationMode, setEvaluationMode] = useState<"local" | "ai" | null>(null);
  const [isEvaluating, setIsEvaluating] = useState<boolean>(false);
  const [evaluatedXp, setEvaluatedXp] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Focus labels & icons
  const focusOptions = [
    { value: 1, label: "Distracted ☁️" },
    { value: 2, label: "Casual ☕" },
    { value: 3, label: "Steady ⚡" },
    { value: 4, label: "Deep Flow 🌀" },
    { value: 5, label: "Zen Master ☯️" },
  ];

  // Effort labels & icons
  const effortOptions = [
    { value: 1, label: "Trivial 🍃" },
    { value: 2, label: "Easy 🪵" },
    { value: 3, label: "Standard ⛰️" },
    { value: 4, label: "Hardcore 🌋" },
    { value: 5, label: "Legendary 🌠" },
  ];

  function runLocalAlgorithm() {
    setErrorMsg(null);
    const base = habit.xp_value || 10;
    const focusModifier = focusLevel / 3.0;
    const diffModifier = difficulty / 3.0;
    
    // Add some random bonus factor (unexpected fun)
    const randomBonus = Math.floor(Math.random() * 5); // 0-4 XP
    const calculated = Math.round(base * focusModifier * diffModifier) + randomBonus;
    
    // Clamp between 5 and 50 XP
    const finalXp = Math.max(5, Math.min(50, calculated));
    
    setEvaluatedXp(finalXp);
    setFeedback("Quest complete. Your local diagnostics confirm steady cognitive progression.");
    setEvaluationMode("local");
  }

  async function consultAiOracle() {
    if (!reflection.trim()) {
      setErrorMsg("Error: Please write a brief reflection first so the AI Oracle can evaluate your mind.");
      return;
    }
    setErrorMsg(null);
    setIsEvaluating(true);
    setEvaluationMode(null);

    try {
      const res = await fetchJson<{ xp: number; feedback: string }>("/api/ai/evaluate-reflection", {
        method: "POST",
        body: JSON.stringify({
          habitName: habit.name,
          focusLevel,
          difficulty,
          reflection,
          baseXp: habit.xp_value || 10,
        }),
      });

      setEvaluatedXp(res.xp);
      setFeedback(res.feedback);
      setEvaluationMode("ai");
    } catch (err: any) {
      console.error(err);
      setErrorMsg("Could not contact the AI Oracle. Falling back to local algorithm.");
      // Fallback
      runLocalAlgorithm();
    } finally {
      setIsEvaluating(false);
    }
  }

  function handleFinalSubmit() {
    const finalXp = evaluatedXp ?? habit.xp_value ?? 10;
    const finalNotes = reflection.trim()
      ? `[Reflection: Focus=${focusLevel}, Difficulty=${difficulty}] ${reflection.trim()} (${feedback || ""})`
      : `[Reflection: Focus=${focusLevel}, Difficulty=${difficulty}]`;
    onSubmit(finalXp, finalNotes);
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(6px)",
        padding: "1rem",
        fontFamily: "JetBrains Mono, monospace",
      }}
    >
      <div
        className="shukan-card"
        style={{
          width: "100%",
          maxWidth: "480px",
          backgroundColor: "var(--bg-elevated)",
          border: "1px solid var(--accent)",
          boxShadow: "0 0 20px var(--accent-glow)",
          padding: "1.25rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px dashed var(--border)", paddingBottom: "0.5rem" }}>
          <span style={{ color: "var(--accent)", fontWeight: "bold", fontSize: "0.85rem" }}>
            &gt; QUEST REFLECTION TERMINAL
          </span>
          <button
            type="button"
            onClick={onClose}
            className="shukan-btn"
            style={{ padding: "0.1rem 0.4rem", fontSize: "0.7rem", borderColor: "transparent", color: "var(--fg-dim)" }}
          >
            [close]
          </button>
        </div>

        {/* Quest Info */}
        <div style={{ fontSize: "0.8rem" }}>
          <span>QUEST: </span>
          <strong style={{ color: "var(--fg)" }}>{habit.name}</strong>
          <span style={{ color: "var(--fg-dim)", marginLeft: "0.5rem" }}>(base reward: {habit.xp_value}xp)</span>
        </div>

        {/* Focus Selector */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <label style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>$ select focus level</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "0.25rem" }}>
            {focusOptions.map((opt) => {
              const isSelected = focusLevel === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setFocusLevel(opt.value);
                    setEvaluationMode(null);
                  }}
                  className={`shukan-btn ${isSelected ? "shukan-btn-primary" : ""}`}
                  style={{
                    padding: "0.3rem 0.1rem",
                    fontSize: "0.55rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0.15rem",
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                  }}
                >
                  <span style={{ fontSize: "0.7rem" }}>{opt.label.split(" ")[1]}</span>
                  <span>{opt.label.split(" ")[0]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Effort Selector */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <label style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>$ select effort / intensity</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "0.25rem" }}>
            {effortOptions.map((opt) => {
              const isSelected = difficulty === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setDifficulty(opt.value);
                    setEvaluationMode(null);
                  }}
                  className={`shukan-btn ${isSelected ? "shukan-btn-primary" : ""}`}
                  style={{
                    padding: "0.3rem 0.1rem",
                    fontSize: "0.55rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0.15rem",
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                  }}
                >
                  <span style={{ fontSize: "0.7rem" }}>{opt.label.split(" ")[1]}</span>
                  <span>{opt.label.split(" ")[0]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Reflection text area */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <label style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>$ reflection (what did you learn/accomplish?)</label>
          <textarea
            className="shukan-input"
            rows={3}
            style={{ fontSize: "0.75rem", fontFamily: "monospace" }}
            placeholder="E.g. studied 30 vocabulary terms and wrote 5 sentences. Feeling confident."
            value={reflection}
            onChange={(e) => {
              setReflection(e.target.value);
              setEvaluationMode(null);
            }}
          />
        </div>

        {/* Action Panel */}
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem" }}>
          <button
            type="button"
            onClick={runLocalAlgorithm}
            disabled={isEvaluating}
            className="shukan-btn"
            style={{ flex: 1, fontSize: "0.7rem", padding: "0.4rem 0" }}
          >
            [ local review ]
          </button>
          <button
            type="button"
            onClick={consultAiOracle}
            disabled={isEvaluating}
            className="shukan-btn"
            style={{ flex: 1, fontSize: "0.7rem", padding: "0.4rem 0", borderColor: "var(--accent)" }}
          >
            {isEvaluating ? "$ analyzing..." : "[ consult AI oracle ]"}
          </button>
        </div>

        {/* Error Messages */}
        {errorMsg && (
          <div style={{ fontSize: "0.7rem", color: "var(--danger)" }}>
            {errorMsg}
          </div>
        )}

        {/* Evaluation Output */}
        {evaluationMode && (
          <div
            style={{
              border: "1px dashed var(--border)",
              backgroundColor: "rgba(255,255,255,0.01)",
              padding: "0.75rem",
              fontSize: "0.75rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.4rem",
              animation: "fadeIn 0.2s ease",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "var(--fg-dim)" }}>
                EVALUATION METHOD: <span style={{ color: "var(--accent)" }}>{evaluationMode.toUpperCase()}</span>
              </span>
              <strong style={{ color: "var(--accent)", fontSize: "0.85rem" }}>
                🏆 +{evaluatedXp} XP
              </strong>
            </div>
            
            {feedback && (
              <div style={{ fontStyle: "italic", color: "var(--fg)" }}>
                &gt; "{feedback}"
              </div>
            )}
            
            <button
              type="button"
              onClick={handleFinalSubmit}
              className="shukan-btn shukan-btn-primary"
              style={{ width: "100%", marginTop: "0.4rem", padding: "0.5rem 0", fontSize: "0.75rem" }}
            >
              $ CLAIM REWARD & COMPLETE QUEST
            </button>
          </div>
        )}

        {/* Fallback Cancel / Normal Log */}
        {!evaluationMode && !isEvaluating && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px dashed var(--border)", paddingTop: "0.5rem" }}>
            <span style={{ fontSize: "0.65rem", color: "var(--fg-dim)" }}>
              Want to skip reflection?
            </span>
            <button
              type="button"
              onClick={() => onSubmit(habit.xp_value || 10, "")}
              className="shukan-btn"
              style={{ fontSize: "0.65rem", padding: "0.15rem 0.4rem" }}
            >
              [ skip & log default +{habit.xp_value || 10}xp ]
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
